import { useSearchParams } from "react-router-dom";
import React from "react";
import { Sparkles, Users, Star, Layers } from "lucide-react";

/**
 * Central definition of the shared "segment" chip filter used on Mapa + Tablero.
 * The chip is persisted in the URL query (?seg=oport|dueno|top|todo) so a link
 * shared between team-mates preserves the segment.
 */
export const SEGMENT_CHIPS = [
  {
    id: "oport",
    label: "Oportunidades",
    icon: Sparkles,
    // Curated by the account rules — es_subvaluado includes the % and umbral logic.
    match: (o) => o.es_subvaluado,
    testid: "chip-oport",
  },
  {
    id: "dueno",
    label: "Dueño directo",
    icon: Users,
    match: (o) => o.es_dueno_directo,
    testid: "chip-dueno",
  },
  {
    id: "top",
    label: "Top",
    icon: Star,
    match: (o) => o.es_subvaluado && o.es_dueno_directo,
    testid: "chip-top",
  },
  {
    id: "todo",
    label: "Ver todo",
    icon: Layers,
    match: () => true,
    testid: "chip-todo",
  },
];

export function useSegmentChip(defaultId = "oport") {
  const [params, setParams] = useSearchParams();
  const raw = params.get("seg");
  const current = SEGMENT_CHIPS.find((c) => c.id === raw)?.id || defaultId;
  const setCurrent = (id) => {
    const next = new URLSearchParams(params);
    if (!id || id === defaultId) next.delete("seg");
    else next.set("seg", id);
    setParams(next, { replace: true });
  };
  const chip = SEGMENT_CHIPS.find((c) => c.id === current) || SEGMENT_CHIPS[0];
  return [chip, setCurrent];
}

export function filterBySegment(opps, chipId) {
  const chip = SEGMENT_CHIPS.find((c) => c.id === chipId) || SEGMENT_CHIPS[0];
  return opps.filter(chip.match);
}

export function segmentCounts(opps) {
  const out = {};
  SEGMENT_CHIPS.forEach((c) => { out[c.id] = 0; });
  opps.forEach((o) => {
    SEGMENT_CHIPS.forEach((c) => { if (c.match(o)) out[c.id] += 1; });
  });
  return out;
}

/** ChipBar — small, purely presentational; owner controls active/counts. */
export default function ChipBar({ current, counts, onChange, className = "" }) {
  return (
    <div className={`flex flex-wrap gap-2 ${className}`} data-testid="segment-chipbar">
      {SEGMENT_CHIPS.map((c) => {
        const active = c.id === current;
        const Icon = c.icon;
        const n = counts?.[c.id] ?? 0;
        return (
          <button
            key={c.id}
            type="button"
            data-testid={c.testid}
            data-active={active ? "true" : "false"}
            onClick={() => onChange(c.id)}
            className={
              "flex items-center gap-2 px-3.5 py-2 text-xs font-heading font-bold uppercase tracking-[0.06em] " +
              "border transition-all duration-150 " +
              (active
                ? "bg-emerald-700 text-white border-emerald-800 shadow-md shadow-emerald-700/25"
                : "bg-white text-slate-700 border-slate-300 hover:border-emerald-500 hover:text-emerald-800")
            }
          >
            <Icon className="w-3.5 h-3.5" />
            <span>{c.label}</span>
            <span
              className={
                "ml-1 px-1.5 py-0.5 rounded-none text-[10px] font-mono font-bold min-w-[24px] text-center " +
                (active
                  ? "bg-emerald-900/40 text-white"
                  : "bg-slate-100 text-slate-600 group-hover:bg-emerald-50")
              }
              data-testid={`${c.testid}-count`}
            >
              {n}
            </span>
          </button>
        );
      })}
    </div>
  );
}
